import { Check, Loader, X, type LucideIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { useAuth } from "~/context/AuthContext";
import { useSubscription } from "~/context/SubscriptionContext";
import { useIsAgencyClient, useTeamAgency } from "~/hooks/useTeamAccess";
import { apiClient } from "~/lib/api-client";
import { billingPath, subscribePath } from "~/lib/billing-paths";
import { getPartnerDiscountCode } from "~/lib/partner-cookies";
import { isPastDueSubscription } from "~/lib/subscription-status";
import { Link } from "~/lib/team-router";
import { cn } from "~/lib/utils";
import type { BillingPlan } from "~/types/BillingTypes";

export type UpgradePlanSlug = "starter" | "pro" | "business";
type BillingInterval = "monthly" | "annual";

const PLAN_RANK: Record<string, number> = {
  trial: 0,
  starter: 1,
  pro: 2,
  business: 3,
};

export function resolveRecommendedPlan(
  minimumSlug: UpgradePlanSlug,
  currentSlug?: string | null
): UpgradePlanSlug {
  const current = (currentSlug || "").toLowerCase();
  const currentRank = PLAN_RANK[current] ?? 0;
  const minimumRank = PLAN_RANK[minimumSlug];

  if (currentRank >= minimumRank) {
    if (minimumSlug === "starter") {
      if (current === "pro") return "pro";
      if (current === "business") return "business";
      return "starter";
    }
    if (current === "pro") return "business";
    return "business";
  }

  return minimumSlug;
}

function planHighlights(plan: BillingPlan): string[] {
  const slug = plan.slug;
  const prompts =
    typeof plan.max_prompts_per_month === "number" &&
    plan.max_prompts_per_month > 0
      ? plan.max_prompts_per_month
      : slug === "business"
        ? 200
        : 100;
  const seats =
    typeof plan.max_team_seats === "number" && plan.max_team_seats > 0
      ? plan.max_team_seats
      : slug === "business"
        ? 5
        : 3;

  const lines = [
    `${prompts.toLocaleString()} AI Visibility prompts`,
    seats === 1 ? "1 team seat" : `Up to ${seats} team seats`,
    `${plan.monthly_ai_agent_credits.toLocaleString()} AI Agent credits / cycle`,
  ];

  if (plan.keyword_clustering !== false) {
    lines.push("Keyword clustering");
  }
  if (
    plan.ai_traffic_analytics === true ||
    (plan.ai_traffic_analytics === undefined &&
      (slug === "pro" || slug === "business"))
  ) {
    lines.push("AI Traffic analytics");
  }

  return lines.slice(0, 4);
}

export interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description: string;
  recommendedPlan: UpgradePlanSlug;
  icon?: LucideIcon;
  secondaryLabel?: string;
}

export default function UpgradeModal({
  isOpen,
  onClose,
  title,
  description,
  recommendedPlan,
  icon: Icon,
  secondaryLabel = "Not now",
}: UpgradeModalProps) {
  const { user } = useAuth();
  const teamAgency = useTeamAgency();
  const isAgencyClient = useIsAgencyClient();
  const { subscription } = useSubscription();
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [interval, setInterval] = useState<BillingInterval>("annual");
  const [checkingOut, setCheckingOut] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPastDue = isPastDueSubscription(subscription);
  const isAgency = Boolean(teamAgency);
  const currentSlug = subscription?.billing_plan?.slug;
  const planSlug = useMemo(
    () => resolveRecommendedPlan(recommendedPlan, currentSlug),
    [recommendedPlan, currentSlug]
  );

  useEffect(() => {
    if (!isOpen) {
      setInterval("annual");
      setError(null);
      setCheckingOut(false);
      return;
    }

    if (isAgency || isAgencyClient) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [membersRes, plansRes] = await Promise.all([
          apiClient.get("/members"),
          apiClient.get("/billing/plans"),
        ]);
        if (cancelled) return;

        if (membersRes.status === 200) {
          const members: Array<{
            email: string;
            role: string;
            is_creator: boolean;
          }> = membersRes.data?.data || [];
          const currentMember = members.find((m) => m.email === user?.email);
          setIsOwner(
            currentMember?.is_creator === true ||
              currentMember?.role === "owner"
          );
        }

        if (plansRes.status === 200) {
          const raw = (plansRes.data?.data || []) as BillingPlan[];
          setPlans(
            raw.filter((p) => p.slug !== "enterprise" && p.slug !== "bespoke")
          );
        }
      } catch (err) {
        console.error("Failed to load upgrade checkout:", err);
        if (!cancelled) {
          setError("Could not load plan details. Please try again.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, user?.email, isAgency, isAgencyClient]);

  const plan = plans.find((p) => p.slug === planSlug) || null;
  const monthlyEquivalent =
    plan == null
      ? null
      : interval === "monthly"
        ? plan.monthly_price
        : plan.annual_price / 12;
  const billedTotal =
    plan == null
      ? null
      : interval === "monthly"
        ? plan.monthly_price
        : plan.annual_price;
  const highlights = plan ? planHighlights(plan) : [];

  const startCheckout = async () => {
    if (!plan || !isOwner) return;
    setCheckingOut(true);
    setError(null);
    try {
      const discountCode = getPartnerDiscountCode();
      const res = await apiClient.post("/billing/checkout", {
        billing_plan_slug: plan.slug,
        billing_interval: interval,
        success_url: `${window.location.origin}${window.location.pathname}${window.location.search}`,
        ...(discountCode && { discount_code: discountCode }),
      });
      if (res.status === 200 && res.data?.data?.checkout_url) {
        window.location.href = res.data.data.checkout_url;
        return;
      }
      setError("Checkout could not be started. Please try again.");
    } catch (err: unknown) {
      console.error(err);
      const detail =
        err &&
        typeof err === "object" &&
        "responseData" in err &&
        err.responseData &&
        typeof err.responseData === "object" &&
        "detail" in err.responseData
          ? String((err.responseData as { detail?: string }).detail)
          : "Checkout failed. Please try again.";
      setError(detail);
    } finally {
      setCheckingOut(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm text-stone-400 transition-colors hover:text-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-300"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>

        <DialogHeader className="space-y-3">
          {Icon ? (
            <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-stone-200 bg-stone-50">
              <Icon className="h-5 w-5 text-stone-700" />
            </div>
          ) : null}
          <div className="space-y-1.5">
            <DialogTitle className="text-left text-xl font-semibold tracking-tight text-stone-900">
              {isPastDue ? "Payment failed" : title}
            </DialogTitle>
            <DialogDescription className="text-left text-sm leading-relaxed text-stone-500">
              {isPastDue
                ? isAgencyClient
                  ? "We could not charge the card for this workspace. Contact your agency to restore access."
                  : "We could not charge the card for this workspace. Update your payment method to keep using Zerply."
                : description}
            </DialogDescription>
          </div>
        </DialogHeader>

        {isAgencyClient ? (
          <div className="space-y-3 pt-2">
            {!isPastDue && (
              <p className="text-sm text-stone-600">
                Your agency manages the plan for this workspace. Ask them to
                turn this on for you.
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={onClose}
            >
              {secondaryLabel}
            </Button>
          </div>
        ) : isPastDue ? (
          <div className="space-y-3 pt-2">
            <Button asChild className="w-full bg-orange-500 text-white hover:bg-orange-600">
              <Link to={billingPath(isAgency)} onClick={onClose}>
                Update payment
              </Link>
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full text-stone-500 hover:text-stone-800"
              onClick={onClose}
            >
              {secondaryLabel}
            </Button>
          </div>
        ) : isAgency ? (
          <div className="space-y-3 pt-2">
            <p className="text-sm text-stone-600">
              Agency billing lives on the agency plan. Open agency billing to
              compare Premium, Elite, and Ultimate.
            </p>
            <Button asChild className="w-full bg-orange-500 text-white hover:bg-orange-600">
              <Link to={subscribePath(true)} onClick={onClose}>
                View agency plans
              </Link>
            </Button>
            <Button
              type="button"
              variant="ghost"
              className="w-full text-stone-500 hover:text-stone-800"
              onClick={onClose}
            >
              {secondaryLabel}
            </Button>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader className="h-5 w-5 animate-spin text-stone-400" />
          </div>
        ) : !plan ? (
          <p className="py-6 text-sm text-stone-600">
            {error || "This plan is unavailable right now."}
          </p>
        ) : !isOwner ? (
          <div className="space-y-4 pt-2">
            <p className="text-sm text-stone-600">
              Ask a workspace owner to upgrade to {plan.name}. Only owners can
              complete checkout.
            </p>
            <Button type="button" variant="outline" className="w-full" onClick={onClose}>
              {secondaryLabel}
            </Button>
          </div>
        ) : (
          <div className="space-y-4 pt-1">
            <div className="inline-flex rounded-md border border-stone-200 bg-white p-0.5">
              <button
                type="button"
                onClick={() => setInterval("annual")}
                className={cn(
                  "rounded px-2 py-1 text-xs font-medium transition-colors",
                  interval === "annual"
                    ? "bg-stone-900 text-white"
                    : "text-stone-500 hover:text-stone-800"
                )}
              >
                Annual
                <span className="ml-1 text-[10px] font-normal opacity-80">
                  save ~17%
                </span>
              </button>
              <button
                type="button"
                onClick={() => setInterval("monthly")}
                className={cn(
                  "rounded px-2 py-1 text-xs font-medium transition-colors",
                  interval === "monthly"
                    ? "bg-stone-900 text-white"
                    : "text-stone-500 hover:text-stone-800"
                )}
              >
                Monthly
              </button>
            </div>

            <div className="rounded-xl border border-stone-200 bg-stone-50/80 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-medium tracking-wide text-stone-500 uppercase">
                    Recommended
                  </p>
                  <p className="mt-1 text-lg font-semibold text-stone-900">
                    {plan.name}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-semibold tabular-nums text-stone-900">
                    ${monthlyEquivalent?.toFixed(0)}
                    <span className="text-sm font-normal text-stone-500">
                      /mo
                    </span>
                  </p>
                  <p className="text-xs text-stone-500">
                    {interval === "annual"
                      ? `$${billedTotal?.toFixed(0)} billed annually`
                      : "Billed monthly"}
                  </p>
                </div>
              </div>

              <ul className="mt-4 space-y-2">
                {highlights.map((line) => (
                  <li
                    key={line}
                    className="flex items-start gap-2 text-sm text-stone-600"
                  >
                    <Check
                      className="mt-0.5 h-4 w-4 shrink-0 text-stone-700"
                      strokeWidth={2}
                    />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </div>

            {error ? (
              <p className="text-sm text-red-600">{error}</p>
            ) : null}

            <div className="flex flex-col gap-2">
              <Button
                type="button"
                onClick={() => void startCheckout()}
                disabled={checkingOut}
                className="w-full bg-orange-500 text-white hover:bg-orange-600"
              >
                {checkingOut ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  `Continue to checkout · ${plan.name}`
                )}
              </Button>
              <Button
                type="button"
                variant="ghost"
                className="w-full text-stone-500 hover:text-stone-800"
                onClick={onClose}
              >
                {secondaryLabel}
              </Button>
              <p className="text-center text-xs text-stone-400">
                Prefer to compare options?{" "}
                <Link
                  to={subscribePath(false)}
                  className="font-medium text-stone-600 underline-offset-2 hover:text-stone-900 hover:underline"
                  onClick={onClose}
                >
                  See all plans
                </Link>
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
